import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useQuery } from "@tanstack/react-query";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

export interface UserProfile {
  id: string;
  display_name: string;
  display_color: string;
  created_at: string;
  updated_at: string;
}

export const useUserProfiles = (userIds: string[]) => {
  // Filter out undefined/null values to prevent UUID parsing errors
  const validUserIds = userIds.filter((id): id is string => Boolean(id));

  return useQuery({
    queryKey: ["user-profiles", validUserIds],
    queryFn: async (): Promise<Record<string, UserProfile>> => {
      if (validUserIds.length === 0) return {};

      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id, display_name, display_color, created_at, updated_at")
        .in("id", validUserIds);

      if (error) {
        console.error("Error fetching user profiles:", error);
        return {};
      }

      if (!profiles) return {};

      return profiles.reduce(
        (acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        },
        {} as Record<string, UserProfile>,
      );
    },
    enabled: validUserIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUserProfile = (userId?: string) => {
  return useUserProfiles(userId ? [userId] : []);
};
